import { Button, FormControl, NativeSelect, OutlinedInput, TextField, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { handleCreateNewEntity, handleGetCompanies, handleUpdateEntity } from "../../../api/mosaic/accounts";
import { ICompany } from "./UploadFileTabPanel/interfaces";
import { EntityType, FormAction } from "../../../api/mosaic/interface";
import ModalWrapper, { modalCancelButtonSx, modalSubmitButtonSx } from "../../shared/ModalWrapper";
import { useToast } from "../../shared/Toast";

interface ISimpleModal {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDoneSaving: React.Dispatch<React.SetStateAction<boolean>>;
  rowData?: { id?: number; name: string; company_id?: number | string };
  kind?: FormAction;
  type: EntityType;
}

const selectSx = { borderRadius: "8px" };

const SimpleModal = ({
  open,
  setOpen,
  setIsDoneSaving,
  rowData,
  kind,
  type,
}: ISimpleModal) => {
  const [companies, setCompanies] = useState<ICompany[]>();
  const [selectedCompany, setSelectedCompany] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const { showSuccess, showError } = useToast();

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (kind === FormAction.CREATE) {
        await handleCreateNewEntity(type, { name, company_id: selectedCompany });
      } else if (kind === FormAction.UPDATE && rowData) {
        await handleUpdateEntity(type, { id: rowData.id, name, company_id: selectedCompany });
      }
      setIsLoading(false);
      setIsDoneSaving((prev) => !prev);
      setOpen(false);
      showSuccess(`${type.charAt(0).toUpperCase() + type.slice(1)} ${kind === FormAction.CREATE ? "created" : "updated"} successfully`);
    } catch (err) {
      setIsLoading(false);
      showError(`Error: ${type} might already exist`);
      console.error(err);
    }
  };

  useEffect(() => {
    if (rowData) {
      setName(rowData.name);
      setSelectedCompany(rowData.company_id ? Number(rowData.company_id) : undefined);
    }
  }, [rowData]);

  useEffect(() => {
    const getCompanies = async () => {
      try {
        const response = await handleGetCompanies();
        setCompanies(response.data.data as ICompany[]);
      } catch (error) {
        console.error(error);
      }
    };
    getCompanies();
  }, []);

  return (
    <ModalWrapper
      open={open}
      onClose={handleClose}
      title={kind === FormAction.CREATE ? `Add new ${type}` : `Update ${type}`}
      actions={
        <>
          <Button variant="outlined" onClick={handleClose} sx={modalCancelButtonSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={modalSubmitButtonSx}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Name *"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 100 }}
        />
        <FormControl fullWidth>
          <NativeSelect
            value={selectedCompany}
            input={<OutlinedInput sx={selectSx} />}
            onChange={(e) => setSelectedCompany(Number(e.target.value))}
          >
            <option value="">Select a company *</option>
            {companies?.map((result, index) => (
              <option value={result.id} key={index}>{result.name}</option>
            ))}
          </NativeSelect>
        </FormControl>
      </Box>
    </ModalWrapper>
  );
};

export default SimpleModal;
